import { useCallback, useEffect, useState } from 'react';

import { isEmpty } from 'lodash';

import {
  ClaimDocumentMetadata,
  ClaimDocumentsRequest,
  ClaimDocumentsResponse,
  PaginationMetadata,
} from '~/components/Chat/types';
import { post } from '~/utils/request';

export interface UseDocumentsParams {
  claimNumber: string;
}

export interface UseDocumentsReturn {
  documents: ClaimDocumentMetadata[];
  pagination: PaginationMetadata | null;
  reloadDocuments: () => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
}

export const useClaimDocuments = ({ claimNumber }: UseDocumentsParams): UseDocumentsReturn => {
  const [documents, setDocuments] = useState<ClaimDocumentMetadata[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (isEmpty(claimNumber)) {
      setIsError(true);
      setError('No claim number available');
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const response = await post<ClaimDocumentsResponse, ClaimDocumentsRequest>({
        url: `/api/v1/claims/${claimNumber}/documents`,
        data: {
          limit: 100,
          document_types: ['textual_file', 'image', 'email_file', 'text', 'xls', 'sms', 'email'],
        },
      });

      setDocuments(response.documents);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch claim documents';
      console.error('Failed to fetch claim documents:', err);
      setIsError(true);
      setError(errorMessage);
      setDocuments([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [claimNumber]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return {
    documents,
    pagination,
    reloadDocuments: loadDocuments,
    isLoading,
    isError,
    error,
  };
};
